import { filterErrorsByField } from 'Automations/automationEditor/utils'
import { Box, Grid } from '@sequencehq/core-components'
import { Fragment } from 'react'

import { ConditionPropertyEditorComponent } from 'Automations/nodeDefinitions/conditions'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import { ProductSelector } from '../../../../switch/components/GroupEditor/inputs/components/ProductSelector'
import {
  appendTestId,
  hasCommittedDiscountSibling,
  readValue,
  usePrimaryOperatorState
} from './ProductPropertyEditor.helpers'

type HasDiscountOption = 'yes' | 'no'

const HAS_DISCOUNT_OPTIONS: Array<{ label: string; value: HasDiscountOption }> =
  [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' }
  ]

const hasDiscountToOption = (
  hasDiscount: boolean | undefined
): HasDiscountOption | '' => {
  if (hasDiscount === true) return 'yes'
  if (hasDiscount === false) return 'no'
  return ''
}

/**
 * Editor for the PRODUCT.HAS_DISCOUNT operator. Owns the product picker,
 * primary operator dropdown (DISCOUNT / HAS_DISCOUNT), and the second
 * row holding the yes/no answer.
 */
export const HasDiscountOperatorEditor: ConditionPropertyEditorComponent = ({
  'data-testid': dataTestId,
  operator,
  value,
  onChange,
  operators,
  siblingConditions,
  validationErrors,
  focusOnInitialLoad
}) => {
  const { productId, hasDiscount } = readValue(value)

  const {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    sameProductSiblings
  } = usePrimaryOperatorState({
    operator,
    productId,
    operators,
    siblingConditions,
    onChange
  })

  // When a sibling DISCOUNT row already committed to a comparator on this
  // product, "this product has a discount" is the only consistent answer.
  const onlyYesAllowed = hasCommittedDiscountSibling(sameProductSiblings)
  const hasDiscountOptions = onlyYesAllowed
    ? HAS_DISCOUNT_OPTIONS.filter(o => o.value === 'yes')
    : HAS_DISCOUNT_OPTIONS

  return (
    <Fragment>
      <Grid
        gridTemplateColumns="1fr 1fr"
        gap="8px"
        gridTemplateAreas='"product operator" "subRow subRow"'
      >
        <Box gridArea="product">
          <ProductSelector
            data-testid={appendTestId(dataTestId, 'product')}
            value={productId ?? ''}
            onChange={selected =>
              onChange({
                value: {
                  productId: String(selected),
                  hasDiscount: hasDiscount ?? true
                }
              })
            }
            validationErrors={filterErrorsByField(
              validationErrors?.value,
              'productId'
            )}
            initiallyOpen={focusOnInitialLoad}
          />
        </Box>
        <Box gridArea="operator">
          <InputSelectControlField
            data-testid={appendTestId(dataTestId, 'operator')}
            placeholder="Select..."
            options={operatorOptions}
            value={operator ?? ''}
            disabled={primaryOperatorDisabled}
            onChange={onPrimaryOperatorChange}
            validationErrors={validationErrors?.operator}
          />
        </Box>
        {!!productId && (
          <Grid
            data-testid={appendTestId(dataTestId, 'subRow')}
            gridArea="subRow"
            gridTemplateColumns={SUB_OPERATOR_COLUMN_WIDTH}
            columnGap="8px"
          >
            <InputSelectControlField
              data-testid={appendTestId(dataTestId, 'hasDiscount')}
              value={hasDiscountToOption(hasDiscount)}
              onChange={selected =>
                onChange({
                  value: {
                    productId: productId ?? '',
                    hasDiscount: selected === 'yes'
                  }
                })
              }
              options={hasDiscountOptions}
              placeholder="Select..."
              disabled={!productId}
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'hasDiscount'
              )}
            />
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
